import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from '@angular/router';
import { JwtAuthService } from '../services/auth/jwt-auth.service';
import { forkJoin, Observable, of, switchMap } from 'rxjs';
import { map } from 'rxjs/operators';
import { OrganizationService } from '../services/organization.service';
import { OrganizationEntityService } from '../../state/entity-services/organization-entity.service';
import { FaviconService } from '../services/favicon.service';
import { CyclesEntityService } from '../../state/entity-services/cycles-entity.service';
import { FeatureFlagService } from '../services/feature-flag.service';
import { AuthStatements } from '../../state/models/auth-statements';
import { AclService } from '../services/acl.service';
import { AmplitudeService } from '../services/amplitude.service';
import { DateService } from '../services/date.service';

@Injectable()
export class OrganizationInitialGuard implements CanActivate {
  constructor(
    private dateService: DateService,
    private amplitudeService: AmplitudeService,
    private aclService: AclService,
    private featureFlagService: FeatureFlagService,
    private cyclesEntityService: CyclesEntityService,
    private faviconService: FaviconService,
    private organizationService: OrganizationService,
    private organizationEntityService: OrganizationEntityService,
    private router: Router, private jwtAuthService: JwtAuthService) {
  }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    if (!this.jwtAuthService.isLoggedIn) {
      this.router.navigate(['/sessions/login'], {
        queryParams: {
          return: state.url,
        },
      });
      return of(false);
    }

    const organizationId = this.findOrganization(route);
    if (!organizationId) {
      return of(true);
    }

    return forkJoin(
      [
        this.organizationEntityService.getById(organizationId),
        this.cyclesEntityService.getCurrentCycleId(organizationId),
        this.featureFlagService.getFeatureFlagSearchesByOrgId(parseInt(organizationId, 10)),
      ]
    ).pipe(
      map(response => {
        const [organization, cycle] = response;
        this.organizationService.setOrganization(organization);
        this.organizationService.setCycles(cycle);
        this.faviconService.changeFavicon(organization.favicon);
        return true;
      }),
      switchMap(() => {
        return this.aclService.getRoles(this.jwtAuthService.getUser()?.id);
      }),
      switchMap(roles => {
        const { permissions, authStatements } = roles;
        const orgIds = this.getUniqueOrgIds(authStatements);
        return this.organizationEntityService.getWithQuery({ ids: orgIds.join(',') })
          .pipe(
            map((response) => {
              if (this.isAdmin(permissions)) {
                return roles;
              }

              const user = this.jwtAuthService.getUser();
              const orgIds = response.map(org => org.id.toString());
              const orgNames = response.map(org => org.name);
              const orgTypes = response.map(org => org.type);
              const userType = authStatements.map(as => {
                return as.role.name;
              });

              this.amplitudeService.addIdentifyEventArray('user_type', userType);
              this.amplitudeService.addIdentifyEventArray('permission_org_id', orgIds);
              this.amplitudeService.addIdentifyEventArray('permission_org_name', orgNames);
              this.amplitudeService.addIdentifyEventArray('permission_org_type', orgTypes);
              this.amplitudeService.setUserId(user.id.toString());

              const { birthdate, biologicalSex } = user.personProperties;
              this.amplitudeService.addIdentifyEvent('biological_sex', biologicalSex);
              this.amplitudeService.addIdentifyEvent('age', this.dateService.diffByYears(birthdate).toString());

              this.amplitudeService.setIdentify();
              return roles;
            })
          );
      }),
      switchMap(roles => {
        const { permissions, authStatements } = roles;
        if (this.isAdmin(permissions)) {
          this.organizationService.setInfoPermissions({ hasAdmin: true }, authStatements);
          return of(true);
        }
        const orgIds = this.getUniqueOrgIds(authStatements);
        return this.organizationEntityService.getWithQuery({ idsOrRelatedOrgIds: orgIds.join(',') }).pipe(
          map((organizations) => {
            this.organizationService.setInfoPermissions({
              hasManyOrgs: organizations.length > 1,
              hasAdmin: false
            }, authStatements);
            return true;
          })
        );
      }),
    );
  }

  private isAdmin(permissions: { name } []): boolean {
    return permissions.some(as => as.name === 'ALL_ORGANIZATIONS');
  }


  private getUniqueOrgIds(authStatements: AuthStatements[]): number[] {
    return authStatements
      .filter(as => ['MANAGER', 'DOCTOR'].includes(as.role.name))
      .map(as => as?.resource?.organization?.id)
      .filter((id, index, self) => id && self.indexOf(id) === index);
  }

  private findOrganization(route: ActivatedRouteSnapshot): string | undefined {
    return route?.params['organizationId'] || route?.children && this.findOrganization(route.children[0]);
  }
}


